import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import GlobalStyles from "@mui/material/GlobalStyles";
import TipsAndUpdatesOutlinedIcon from "@mui/icons-material/TipsAndUpdatesOutlined";
import { alpha, styled } from "@mui/material/styles";

const tutorialGradient = (primaryColor: string) =>
  `linear-gradient(135deg, ${alpha(primaryColor, 0.09)}, transparent 65%)`;

// Driver.js appends its card to document.body, outside the provider's DOM tree.
export function TutorialPopoverStyles() {
  return <GlobalStyles styles={(theme) => ({
    ".driver-popover.tutorial-popover": {
      backgroundColor: theme.palette.background.paper,
      backgroundImage: tutorialGradient(theme.palette.primary.main),
      border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
      borderRadius: 12,
      boxShadow: `0 8px 32px ${alpha(theme.palette.common.black, 0.18)}`,
      color: theme.palette.text.primary,
      minWidth: 250,
      maxWidth: "min(300px, calc(100vw - 24px))",
      padding: theme.spacing(1.75),
      "& *": { fontFamily: theme.typography.fontFamily },
      // Use a solid tint here: shrinking the card's gradient into a 10px arrow
      // makes its visible bottom triangle fade to white.
      "& .driver-popover-arrow": {
        borderColor: "transparent",
        "&::before": {
          content: '""',
          position: "absolute",
          inset: -5,
          backgroundColor: theme.palette.background.paper,
          backgroundImage: `linear-gradient(${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.main, 0.08)})`,
        },
        "&.driver-popover-arrow-side-left::before": {
          clipPath: "polygon(0 0, 50% 50%, 0 100%)",
        },
        "&.driver-popover-arrow-side-right::before": {
          clipPath: "polygon(100% 0, 50% 50%, 100% 100%)",
        },
        "&.driver-popover-arrow-side-top::before": {
          clipPath: "polygon(0 0, 100% 0, 50% 50%)",
        },
        "&.driver-popover-arrow-side-bottom::before": {
          clipPath: "polygon(0 100%, 100% 100%, 50% 50%)",
        },
      },
      "& .driver-popover-title": {
        fontSize: theme.typography.pxToRem(16),
        fontWeight: 700,
        lineHeight: 1.4,
        paddingRight: theme.spacing(2),
        letterSpacing: "-0.01em",
      },
      "& .driver-popover-description": {
        color: theme.palette.text.secondary,
        fontSize: theme.typography.pxToRem(13),
        lineHeight: 1.55,
      },
      "& .tutorial-popover-footer": {
        alignItems: "center",
        borderTop: `1px solid ${theme.palette.divider}`,
        display: "flex",
        flexWrap: "wrap",
        gap: theme.spacing(0.75),
        marginTop: theme.spacing(1.25),
        paddingTop: theme.spacing(1),
      },
      "& .driver-popover-progress-text": {
        backgroundColor: alpha(theme.palette.primary.main, 0.1),
        borderRadius: 12,
        color: theme.palette.primary.main,
        fontSize: theme.typography.pxToRem(11),
        fontWeight: 600,
        padding: theme.spacing(0.25, 0.75),
      },
      "& .tutorial-popover-navigation": { marginLeft: "auto" },
    },
  })} />;
}

export const TutorialButton = styled(Button)(({ theme }) => ({
  borderRadius: 7,
  fontSize: theme.typography.pxToRem(12),
  lineHeight: 1.4,
  minHeight: 26,
  minWidth: 0,
  padding: theme.spacing(0.375, 1),
  textTransform: "none",
  fontWeight: 600,
}));

export const TutorialTipIcon = styled(TipsAndUpdatesOutlinedIcon)(({ theme }) => ({
  color: theme.palette.primary.main,
  fontSize: 18,
  marginLeft: theme.spacing(0.5),
}));

export const TutorialIconButton = styled(IconButton)(({ theme }) => ({
  padding: theme.spacing(0.375),
  "& .MuiSvgIcon-root": {
    fontSize: theme.typography.pxToRem(18),
  },
}));

export const TutorialControlBar = styled(Paper)(({ theme }) => ({
  backgroundImage: `linear-gradient(110deg, ${alpha(theme.palette.primary.main, 0.08)}, transparent)`,
  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
  borderRadius: 24,
  bottom: theme.spacing(2),
  display: "flex",
  left: "50%",
  padding: theme.spacing(0.75),
  position: "fixed",
  transform: "translateX(-50%)",
  zIndex: theme.zIndex.tooltip + 1,
}));

export const TutorialButtonGroup = styled("span")(({ theme }) => ({
  alignItems: "center",
  display: "inline-flex",
  gap: theme.spacing(0.75),
}));

export const TutorialPopoverCloseButton = styled(TutorialIconButton)(({ theme }) => ({
  color: theme.palette.text.secondary,
  position: "absolute",
  right: theme.spacing(0.5),
  top: theme.spacing(0.5),
}));

export const TutorialDisableButton = styled(TutorialButton)(({ theme }) => ({
  color: theme.palette.text.secondary,
  flexShrink: 0,
}));
